import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTitre } from "@shared/hooks/useTitre";
import { joindre, villeDepartement } from "@shared/lib/format";
import { useFiche } from "./hooks";
import { BandeAppel } from "./components/BandeAppel";
import { PresentationProspect } from "./components/PresentationProspect";
import { ListeSignaux } from "./components/ListeSignaux";
import { BlocReserves } from "./components/BlocReserves";
import { ClassementOffres } from "@features/offres/ClassementOffres";
import { PanneauArgumentaire } from "@features/argumentaire/PanneauArgumentaire";
import { SequenceActions } from "@features/actions/SequenceActions";
import { Annexes } from "@features/annexes/Annexes";
import { Historique } from "@features/annexes/Historique";

const ONGLETS = [
  ["fiche", "Fiche"], ["offres", "Offres"], ["argumentaire", "Argumentaire"],
  ["actions", "Plan d'action"], ["annexes", "Annexes"], ["historique", "Historique"]
] as const;

export function PageFiche() {
  const { id = "" } = useParams();
  const { data, isLoading, isError, error } = useFiche(id);
  const [onglet, setOnglet] = useState<string>("fiche");
  useTitre(data?.prospect.raisonSociale);

  if (isLoading) return <p className="etat">Chargement de la fiche…</p>;
  if (isError) {
    return (
      <div className="panel" style={{ margin: 20 }}>
        <p><strong>Fiche indisponible.</strong></p>
        <p className="mut">{(error as Error).message}</p>
        <Link to="/recherche">Revenir à la recherche</Link>
      </div>
    );
  }

  if (!data) return <p className="etat">Fiche introuvable.</p>;
  const f = data;
  const p = f.prospect;

  return (
    <main className="fiche">
      <Link className="btn ghost sm" to="/recherche">‹ Retour à la recherche</Link>

      <div className="entete">
        <div>
          <h1>{p.raisonSociale}</h1>
          <div className="mono mut petit">
            {joindre(p.libelleNaf, villeDepartement(p.ville, p.departement), p.effectifTranche)}
          </div>
        </div>
        <div className="jauge-score">
          <div className="num">{f.scoring.score}</div>
          <div className="lbl">score · segment {f.scoring.segment}</div>
        </div>
      </div>

      {f.perimee && (
        <div className="alerte">
          Cette fiche a {f.joursDepuisScrape} jours. Les constats ont pu changer — relancer une analyse avant d'appeler.
        </div>
      )}

      <BandeAppel prospectId={id} argumentaire={f.argumentaire} />

      <div className="tabs" role="tablist">
        {ONGLETS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={onglet === k} onClick={() => setOnglet(k)}>{l}</button>
        ))}
      </div>

      {onglet === "fiche" && (
        <>
          <PresentationProspect prospect={f.prospect} />
          <ListeSignaux prospectId={id} signaux={f.signaux} />
          <BlocReserves reserves={f.reserves} />
        </>
      )}
      {onglet === "offres" && <ClassementOffres offres={f.offres} />}
      {onglet === "argumentaire" && <PanneauArgumentaire prospectId={id} blocs={f.argumentaire} objections={f.objections} />}
      {onglet === "actions" && <SequenceActions prospectId={id} actions={f.planAction} segment={f.scoring.segment} />}
      {onglet === "annexes" && <Annexes prospect={f.prospect} empreinte={f.empreinte} sources={f.sources} />}
      {onglet === "historique" && <Historique entrees={f.historique} />}
    </main>
  );
}
